import { getAddress, type Address } from "viem";
import { hash as hashSchema } from "./schemas";
import { BridgeJournal } from "./journal";
import type { BridgeDirection } from "../types";
import type { BridgeHistoryClient, BridgeRecord, HistoryRange, HistorySnapshot, HistoryRow, ClaimStatus } from "./types";
import { HistoryApi, HistoryApiError, historyApiBase, type HistoryItem, type ClaimEstimate } from "../api/history";
import { BridgeHistorySync } from "./sync";
import { browserLock } from "./journal";

const side = () => ({ ranges: [] as HistoryRange[], error: null, loading: false });
const terminal = (row: HistoryRow) => ["received", "claimed", "reverted"].includes(row.state);
function lastVerified(row: HistoryRow): HistoryRow["lastVerified"] {
  return row.state !== "checking" && row.state !== "unavailable" && row.checkedAt !== null
    ? { state: row.state, checkedAt: row.checkedAt } : row.lastVerified;
}
const pageSize = { in: 500n, out: 2_000n };
export function mergeRanges(ranges: HistoryRange[]): HistoryRange[] {
  const sorted = [...ranges].sort((a, b) => a.from < b.from ? -1 : a.from > b.from ? 1 : 0);
  const merged: HistoryRange[] = [];
  for (const range of sorted) {
    const last = merged.at(-1);
    if (last && range.from <= last.to + 1n) merged[merged.length - 1] = { from: last.from,
      to: last.to > range.to ? last.to : range.to, endHash: last.to > range.to ? last.endHash : range.endHash };
    else merged.push(range);
  }
  return merged;
}

type RpcWork = (signal: AbortSignal, guard: () => void) => Promise<void>;
type RpcJob = { key: string; work: RpcWork; promise: Promise<void>; resolve(): void };
/** Independent list, bounded RPC and durable HTTP synchronization for one context. */
export class BridgeHistoryController {
  private state: HistorySnapshot = { rows: [], intents: [], sides: { in: side(), out: side() }, busy: false, storageError: null, message: null,
    recoveryReady: false, verifying: [], verificationErrors: {}, actions: [] };
  private listeners = new Set<() => void>();
  private abort: AbortController | null = null;
  private generation = 0;
  private active = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private failures = 0;
  private attempts = new Map<string, number>();
  private claimAttempts = new Map<string, number>();
  private session: { owner: object; revision: number } | undefined;
  private current = () => true;
  private refreshQueued = false;
  private remote = new Map<string, HistoryItem>();
  private cursor: string | null = null;
  private detailAttempts = new Map<string, number>();
  private reports = new Map<string, boolean>();
  private reportedClaims = new Map<string, ClaimStatus>();
  private reportTimes = new Map<string, number>();
  private selected: string | null = null;
  private candidates = new Map<string, BridgeRecord>();
  private context = new AbortController();
  private listTimer: ReturnType<typeof setTimeout> | undefined;
  private postTimer: ReturnType<typeof setTimeout> | undefined;
  private listJobs = new Map<string, Promise<void>>();
  private detailJobs = new Map<string, Promise<void>>();
  private rpcJobs = new Map<string, RpcJob>();
  private rpcQueue: RpcJob[] = [];
  private rpcJob: RpcJob | null = null;
  private postJob: Promise<void> | null = null;
  private postQueued = false;
  private cleanupDeferred = false;
  private observationJob: Promise<void> | null = null;
  private pendingRows = new Map<string, { row: HistoryRow; local: boolean; estimate?: ClaimEstimate | null }>();
  private reportSignatures = new Map<string, string>();
  private checkedInCycle = new Set<string>();
  private rpcHasRead = false;
  private explicitRefreshQueued = false;
  private cursorRevision = 0;
  private get api() { return this.journal.sync?.api; }
  constructor(readonly account: Address, readonly journal: BridgeJournal, private client: BridgeHistoryClient) { this.loadJournal(); }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.state;
  freshlyCompleted = () => {
    const saved = this.journal.read();
    return new Set(this.state.rows.filter(row => terminal(row) && row.checkedAt !== null &&
      Date.now() >= row.checkedAt && Date.now() - row.checkedAt < 300_000 &&
      saved.records.some(record => JSON.stringify(record) === JSON.stringify(row.record))).map(row => row.record.id));
  };
  freshlyCompletedClaims = () => {
    const saved = this.journal.read(), now = Date.now();
    return new Set(saved.claims.filter(intent => intent.outcome ||
      (this.state.claims ?? []).some(row => row.intent.id === intent.id && row.intent.transactionHash === intent.transactionHash &&
        ["claimed", "reverted"].includes(row.state) && row.checkedAt !== null && now >= row.checkedAt && now - row.checkedAt < 300_000) ||
      this.state.rows.some(row => row.record.id === intent.recordId && row.state === "claimed" && row.checkedAt !== null &&
        now >= row.checkedAt && now - row.checkedAt < 300_000 && saved.records.some(record => JSON.stringify(record) === JSON.stringify(row.record))))
      .map(intent => intent.id));
  };
  claimBlocked = () => {
    const saved = this.journal.read(), completed = this.freshlyCompletedClaims();
    return Boolean(!this.state.recoveryReady || this.journal.error || saved.intents.some(intent => !this.journal.reconciled(intent)) || saved.claims.some(intent => !completed.has(intent.id)));
  };
  recoveryBlockReason = (direction: BridgeDirection): string | null => {
    this.journal.read();
    if (this.journal.error) return this.journal.error;
    if (!this.state.recoveryReady) return `Loading recovery storage before ${direction === "in" ? "depositing" : "withdrawing"}.`;
    // Old transfers and claims remain tracked without gating a new transfer.
    return null;
  };
  recoveryBlocked = (direction: BridgeDirection) => this.recoveryBlockReason(direction) !== null;
  notifyJournalChange = () => {
    this.loadJournal();
    if (this.state.busy) this.refreshQueued = true;
    else this.refreshRpc(false).catch(() => {});
    this.requestPost();
  };
  private publish(next: Partial<HistorySnapshot>) {
    this.state = { ...this.state, ...next, storageError: this.journal.error };
    for (const listener of this.listeners) listener();
  }
  private loadJournal() {
    const saved = this.journal.read();
    const rows = new Map(this.state.rows.map(row => [row.record.id, row]));
    const changed = new Set<string>();
    for (const entry of this.journal.sync?.recoveryRows() ?? []) {
      const { record } = entry;
      if (!rows.has(record.id)) rows.set(record.id, { record, state: "checking", checkedAt: null });
      for (const { intent } of entry.claims) if (intent.transactionHash &&
        !saved.claims.some(c => c.id === intent.id) && !this.reportedClaims.has(intent.id)) {
        this.reportedClaims.set(intent.id, { intent, state: "checking", checkedAt: null });
      }
    }
    for (const record of saved.records) {
      const previous = rows.get(record.id);
      if (!previous || JSON.stringify(previous.record) !== JSON.stringify(record)) {
        changed.add(record.id); this.attempts.delete(record.id);
        rows.set(record.id, { record, state: "checking", checkedAt: null });
      }
    }
    const claims = saved.claims.map((intent): ClaimStatus => {
      const previous = changed.has(intent.recordId) ? undefined : this.state.claims?.find(row => row.intent.id === intent.id && row.intent.transactionHash === intent.transactionHash && row.intent.outcome === intent.outcome);
      if (!previous) this.claimAttempts.delete(intent.id);
      return previous ?? { intent, state: intent.transactionHash ? "checking" : "uncertain", checkedAt: null };
    });
    for (const claim of this.reportedClaims.values()) if (!claims.some(c => c.intent.id === claim.intent.id ||
      c.intent.transactionHash && c.intent.transactionHash === claim.intent.transactionHash && c.intent.account === claim.intent.account && c.intent.recordId === claim.intent.recordId)) claims.push(claim);
    this.publish({ rows: [...rows.values()], intents: saved.intents, claims, recoveryReady: true });
  }
  start(current: () => boolean, session?: { owner: object; revision: number }) {
    const sameSession = session !== undefined && session.owner === this.session?.owner && session.revision === this.session?.revision;
    this.stop(); this.context = new AbortController(); this.current = current; this.active = true;
    // Cache lives only in this account/route/RPC controller, never in persisted status.
    // A new wallet identity revision (including A -> B -> A) must reverify everything.
    this.session = session ? { ...session } : undefined;
    if (!sameSession) {
      this.attempts.clear(); this.claimAttempts.clear();
      this.reportedClaims = new Map([...this.reportedClaims].map(([id, claim]) => [id, { ...claim, state: "checking", checkedAt: null, lastVerified: undefined }]));
    }
    const now = Date.now();
    const fresh = (checkedAt: number | null, lifetime: number) => sameSession && checkedAt !== null && now >= checkedAt && now - checkedAt < lifetime;
    this.publish({ rows: this.state.rows.map(row => {
      if (row.state !== "checking" && row.state !== "unavailable" && fresh(row.checkedAt, terminal(row) ? 300_000 : 60_000)) return row;
      this.attempts.delete(row.record.id); return { ...row, state: "checking", checkedAt: null,
        lastVerified: sameSession ? lastVerified(row) : undefined };
    }), claims: this.state.claims?.map(row => {
      if (row.state !== "checking" && row.state !== "unavailable" && fresh(row.checkedAt, ["claimed", "reverted"].includes(row.state) ? 300_000 : 60_000)) return row;
      this.claimAttempts.delete(row.intent.id); return { ...row, state: "checking", checkedAt: null, lastVerified: sameSession ? row.lastVerified : undefined };
    }) ?? [] });
    this.loadJournal();
    window.addEventListener("storage", this.onStorage);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.refresh(false).catch(() => {});
  }
  stop() {
    this.active = false; this.cancelWork();
    window.removeEventListener("storage", this.onStorage);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.publish({ busy: false, verifying: [], actions: [], sides: { in: { ...this.state.sides.in, loading: false }, out: { ...this.state.sides.out, loading: false } } });
    if (this.state.api) this.publishApi({ loading: false, loadingOlder: false, syncing: false });
  }
  private cancelWork() {
    this.generation++; this.context.abort(); this.abort?.abort(); this.abort = null;
    clearTimeout(this.timer); clearTimeout(this.listTimer); clearTimeout(this.postTimer);
    for (const job of this.rpcJobs.values()) job.resolve();
    this.rpcJobs.clear(); this.rpcQueue = []; this.rpcJob = null;
    this.listJobs.clear(); this.detailJobs.clear(); this.postJob = null; this.observationJob = null; this.cleanupDeferred = false;
    this.pendingRows.clear(); this.postQueued = false; this.refreshQueued = false;
    this.explicitRefreshQueued = false;
  }
  private onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(this.journal.prefix)) { this.loadJournal(); this.refreshRpc(false).catch(() => {}); this.requestPost(); }
  };
  private onVisibility = () => {
    if (document.visibilityState === "hidden") {
      this.cancelWork();
      this.publish({ busy: false, verifying: [], actions: [], sides: { in: { ...this.state.sides.in, loading: false }, out: { ...this.state.sides.out, loading: false } } });
      if (this.state.api) this.publishApi({ loading: false, loadingOlder: false, syncing: false });
    } else { this.context = new AbortController(); this.refresh(false).catch(() => {}); }
  };
  private available() { return this.active && this.current() && document.visibilityState !== "hidden" && !this.context.signal.aborted; }
  private contextGuard(generation: number, signal: AbortSignal) {
    signal.throwIfAborted();
    if (!this.available() || generation !== this.generation) throw new Error("BRIDGE_HISTORY_CONTEXT_CHANGED");
  }
  private run(work: RpcWork, key = "background"): Promise<void> {
    if (!this.available()) return Promise.resolve();
    const existing = this.rpcJobs.get(key);
    if (existing) return existing.promise;
    let resolve!: () => void;
    const promise = new Promise<void>(done => { resolve = done; });
    const job = { key, work, promise, resolve };
    this.rpcJobs.set(key, job); this.rpcQueue.push(job);
    this.publish({ actions: [...this.rpcJobs.keys()].filter(id => id !== "background") });
    this.drainRpc(); return promise;
  }
  private drainRpc() {
    if (this.rpcJob || !this.available()) return;
    const job = this.rpcQueue.shift(); if (!job) return;
    this.rpcJob = job; this.runRpc(job).catch(() => {});
  }
  private async runRpc(job: RpcJob) {
    clearTimeout(this.timer);
    const generation = this.generation, abort = new AbortController(); this.abort = abort;
    const guard = () => { abort.signal.throwIfAborted(); this.contextGuard(generation, this.context.signal); };
    this.checkedInCycle.clear(); this.rpcHasRead = false;
    this.publish({ busy: true, ...(job.key !== "background" ? { message: null } : {}) });
    const failuresBefore = this.failures;
    try { await job.work(abort.signal, guard); guard(); if (this.failures === failuresBefore) this.failures = 0; }
    catch {
      if (!abort.signal.aborted && this.current() && generation === this.generation) {
        this.failures++;
        if (job.key === "background") this.verification(this.state.verifying ?? [], false, "Current chain verification is unavailable.");
        else this.publish({ message: "Some bridge data could not be verified. Saved records are retained; refresh to check again." });
      }
    } finally {
      if (generation === this.generation && this.rpcJob === job && this.active) {
        this.rpcJobs.delete(job.key); this.rpcJob = null; job.resolve();
        if (this.cleanupDeferred) {
          this.cleanupDeferred = false;
          this.requestPost();
        }
        this.publish({ busy: false, verifying: [], actions: [...this.rpcJobs.keys()].filter(id => id !== "background") }); this.abort = null;
        const delay = this.refreshQueued ? 0 : Math.min(30_000 * 2 ** this.failures, 120_000);
        this.refreshQueued = false;
        if (this.rpcQueue.length) this.drainRpc();
        else this.timer = setTimeout(() => { this.refreshRpc(false).catch(() => {}); }, delay);
      }
    }
  }
  private merge(records: BridgeRecord[]) {
    const rows = new Map(this.state.rows.map(row => [row.record.id, row]));
    for (const record of records) {
      this.journal.put(record);
      const previous = rows.get(record.id);
      if (!previous || JSON.stringify(previous.record) !== JSON.stringify(record)) {
        this.checkedInCycle.delete(record.id);
        rows.set(record.id, { record, state: "checking", checkedAt: null, reported: previous?.reported });
      }
    }
    this.publish({ rows: [...rows.values()], intents: this.journal.read().intents });
  }
  private async scan(direction: BridgeDirection, older: boolean, signal: AbortSignal, guard: () => void) {
    const before = this.state.sides[direction];
    this.publish({ sides: { ...this.state.sides, [direction]: { ...before, loading: true, error: null } } });
    try {
      const valid: HistoryRange[] = [];
      for (const range of before.ranges) {
        let changed = false;
        try {
          changed = !await this.client.canonical(direction, range.to, range.endHash, signal);
          if (changed) throw new Error("BRIDGE_COVERAGE_CHANGED");
          valid.push(range);
        } catch {
          guard();
          this.publish({ rows: this.state.rows.map(row => {
            if (row.record.direction !== direction || BigInt(row.record.sourceBlock) < range.from || BigInt(row.record.sourceBlock) > range.to) return row;
            this.attempts.delete(row.record.id); return { ...row, state: "unavailable", checkedAt: null,
              lastVerified: changed ? undefined : lastVerified(row) };
          }), message: "Previously scanned coverage changed or could not be verified. Records are retained and checked again." });
        }
        guard();
      }
      const head = await this.client.head(direction, signal); guard();
      const ranges = mergeRanges(valid);
      const newest = ranges.at(-1);
      let to = head.number;
      if (older && newest) {
        // Fill the nearest gap before extending the oldest contiguous page.
        to = ranges.length > 1 ? newest.from - 1n : ranges[0]!.from - 1n;
        if (to < 0n) { this.publish({ sides: { ...this.state.sides, [direction]: { ranges, loading: false, error: null } } }); return; }
      }
      let from = to >= pageSize[direction] ? to - pageSize[direction] + 1n : 0n;
      if (!older && newest && newest.to <= to && to - newest.to < pageSize[direction]) from = newest.to > 12n ? newest.to - 12n : 0n;
      const anchor = to === head.number ? head.hash : await this.client.blockHash(direction, to, signal); guard();
      const records = await this.client.scan(this.account, direction, from, to, signal); guard();
      if (!await this.client.canonical(direction, to, anchor, signal)) throw new Error("BRIDGE_PAGE_REORG"); guard();
      await this.journal.withRecordLock(async () => { guard(); this.merge(records); });
      this.publish({ sides: { ...this.state.sides, [direction]: { ranges: mergeRanges([...ranges, { from, to, endHash: anchor }]), loading: false, error: null } } });
    } catch {
      guard(); this.failures++;
      this.publish({ sides: { ...this.state.sides, [direction]: { ...before, ranges: [], loading: false,
        error: "This network could not be scanned. Previously loaded transfers are retained; coverage must be checked again." } } });
    }
  }
  private async verifyNext(signal: AbortSignal, guard: () => void, ids: string[] = []) {
    this.rpcHasRead = true;
    ids = [...new Set([...ids, ...this.journal.sync?.verificationNeeded() ?? []])];
    const now = Date.now();
    const localRecords = new Map(this.journal.read().records.map(r => [r.id, r]));
    const localIds = new Set(localRecords.keys());
    const records = this.state.rows.filter(row => !this.checkedInCycle.has(row.record.id) && (ids.includes(row.record.id) || (!this.api || !terminal(row) || this.candidates.has(row.record.id) || row.record.id === this.selected || !this.attempts.has(row.record.id) || localIds.has(row.record.id)) && now - (this.attempts.get(row.record.id) ?? 0) >=
      (terminal(row) && !this.candidates.has(row.record.id) ? 300_000 : 30_000))).sort((a, b) =>
        Number(ids.includes(b.record.id)) - Number(ids.includes(a.record.id)) ||
        Number(localIds.has(b.record.id)) - Number(localIds.has(a.record.id)) ||
        Number(b.record.id === this.selected) - Number(a.record.id === this.selected) ||
        (this.attempts.get(a.record.id) ?? 0) - (this.attempts.get(b.record.id) ?? 0)).slice(0, 8).map(row => this.candidates.get(row.record.id) ?? row.record);
    if (!records.length) return;
    for (const row of records) { this.attempts.set(row.id, now); this.checkedInCycle.add(row.id); }
    this.verification(records.map(row => row.id), true);
    const publishVerified = (verified: HistoryRow[]) => this.journal.withRecordLock(async () => {
      guard();
      const map = new Map<string, HistoryRow>();
      const stored = new Map(this.journal.read().records.map(r => [r.id, r]));
      for (const row of verified) {
        const before = localRecords.get(row.record.id), current = stored.get(row.record.id);
        if (current && JSON.stringify(current) !== JSON.stringify(before) && JSON.stringify(current) !== JSON.stringify(row.record)) continue;
        const candidate = this.candidates.get(row.record.id);
        if (candidate && JSON.stringify(candidate) === JSON.stringify(row.record)) {
          if (row.checkedAt === null) { this.verification([row.record.id], false, "The reported update could not be verified. Existing local data is retained."); continue; }
          this.candidates.delete(row.record.id);
        } else if (!this.state.rows.some(previous => JSON.stringify(previous.record) === JSON.stringify(row.record))) continue;
        map.set(row.record.id, row);
        // Only a successful independent chain observation may enter the journal.
        // Completed remote history needs no durable local transaction guard.
        if (row.checkedAt !== null && (!terminal(row) || current)) this.journal.put(row.record);
        // Remember local provenance before the streamed row is queued. The
        // record can disappear in another tab before this batch reaches ETA.
        if (terminal(row) && before && !current) this.reports.delete(row.record.id);
        else if (row.checkedAt !== null || localIds.has(row.record.id))
          this.reports.set(row.record.id, !!current || this.journal.hasLocal(`record:${row.record.id}`));
      }
      this.publish({ rows: this.state.rows.map(row => {
        const next = map.get(row.record.id);
        if (!next) return row;
        return next.state === "unavailable" ? { ...next, reported: row.reported, lastVerified: lastVerified(row) } : { ...next, reported: row.reported };
      }) });
      for (const row of verified) this.verification([row.record.id], false, row.state === "unavailable" ? "Current chain verification is unavailable." : null);
      this.queueObservations(this.state.rows.filter(row => map.has(row.record.id) && this.reports.has(row.record.id)));
    });
    const writes: Promise<void>[] = [];
    const delivered = new Set<string>();
    const verified = await this.client.verify(records, signal, row => {
      guard();
      const write = publishVerified([row]); write.catch(() => {}); writes.push(write); delivered.add(row.record.id);
    }); guard();
    await Promise.all(writes); guard();
    if (verified.some(row => row.state === "unavailable" && !this.candidates.has(row.record.id))) this.failures++;
    const remaining = verified.filter(row => !delivered.has(row.record.id));
    if (remaining.length) await publishVerified(remaining);
    // A public candidate must not starve verification of an existing local or
    // previously verified record, and thereby expire its action guards.
    const fallback = verified.flatMap(row => {
      const previous = this.state.rows.find(old => old.record.id === row.record.id);
      return row.checkedAt === null && this.candidates.has(row.record.id) && previous &&
        JSON.stringify(previous.record) !== JSON.stringify(row.record) && (localIds.has(row.record.id) || previous.checkedAt !== null || previous.lastVerified)
        ? [previous.record] : [];
    });
    if (fallback.length) {
      this.verification(fallback.map(row => row.id), true);
      const restored = await this.client.verify(fallback, signal); guard();
      if (restored.some(row => row.state === "unavailable")) this.failures++;
      await publishVerified(restored);
    }
  }
  private verification(ids: string[], loading: boolean, error?: string | null) {
    const verifying = new Set(this.state.verifying), errors = { ...this.state.verificationErrors };
    for (const id of ids) {
      if (loading) verifying.add(id); else verifying.delete(id);
      if (error === null) delete errors[id]; else if (error) errors[id] = error;
    }
    this.publish({ verifying: [...verifying], verificationErrors: errors });
  }
  private async verifyClaims(signal: AbortSignal, guard: () => void, explicit?: "all" | string) {
    const now = Date.now();
    const local = new Set(this.journal.read().claims.map(c => c.id));
    const conflicts = this.journal.sync?.claimVerificationNeeded() ?? new Set<string>();
    const priority = (row: ClaimStatus) => Number(conflicts.has(row.intent.id)) * 2 + Number(explicit === "all" || row.intent.recordId === explicit || row.intent.recordId === this.selected);
    const candidates = (this.state.claims ?? []).filter(row => row.intent.transactionHash &&
      (priority(row) > 0 || !["claimed", "reverted"].includes(row.state) || row.checkedAt === null || local.has(row.intent.id)) &&
      (priority(row) > 0 || !this.claimAttempts.has(row.intent.id) || now - this.claimAttempts.get(row.intent.id)! >=
        (["reverted", "claimed"].includes(row.state) ? 300_000 : 30_000)))
      .sort((a, b) => priority(b) - priority(a) || (this.claimAttempts.get(a.intent.id) ?? 0) - (this.claimAttempts.get(b.intent.id) ?? 0)).slice(0, 8);
    for (const row of candidates) {
      this.verification([row.intent.recordId], true);
      this.claimAttempts.set(row.intent.id, now);
      const record = this.state.rows.find(item => item.record.id === row.intent.recordId)?.record;
      let state: ClaimStatus["state"] = "unavailable", checkedAt: number | null = null;
      try {
        if (!record || !this.client.verifyClaim) throw new Error("Claim verifier unavailable");
        state = await this.client.verifyClaim(record, row.intent.transactionHash!, signal); guard();
        // Absence of a receipt does not verify a public report's hash ownership.
        if (state === "pending" && !this.journal.read().claims.some(c => c.id === row.intent.id)) state = "unavailable";
        else checkedAt = Date.now();
      } catch { guard(); this.failures++; }
      const previous = row.checkedAt !== null && row.state !== "checking" && row.state !== "unavailable"
        ? { state: row.state, checkedAt: row.checkedAt } : row.lastVerified;
      this.publish({ claims: this.state.claims?.map(item => item.intent.id === row.intent.id ? { ...item, state, checkedAt,
        lastVerified: state === "unavailable" ? previous : undefined } : item) ?? [] });
      const verifiedClaim = this.state.claims?.find(c => c.intent.id === row.intent.id);
      if (verifiedClaim) for (const [id, reported] of this.reportedClaims) {
        if (reported.intent.account === row.intent.account && reported.intent.recordId === row.intent.recordId && reported.intent.transactionHash === row.intent.transactionHash)
          this.reportedClaims.set(id, { ...verifiedClaim, intent: reported.intent });
      }
      if (this.journal.read().claims.some(c => c.id === row.intent.id) || state === "reverted" || state === "claimed")
        this.reports.set(row.intent.recordId, this.reports.get(row.intent.recordId) || this.journal.hasLocal(`record:${row.intent.recordId}`));
      this.verification([row.intent.recordId], false, state === "unavailable" ? "Claim verification is unavailable." : null);
      if (this.reports.has(row.intent.recordId)) this.queueObservations(this.state.rows.filter(item => item.record.id === row.intent.recordId));
    }
  }
  refresh = async (explicit = true) => {
    const list = this.api ? this.requestList(false) : Promise.resolve();
    this.requestPost();
    const rpc = this.refreshRpc(explicit);
    await Promise.all([list, rpc]);
    // A newly loaded head may have queued a follow-up behind an existing RPC batch.
    if (this.refreshQueued && this.available()) await this.refreshRpc(false);
  };
  private refreshRpc = (explicit: boolean, changed = false) => {
    this.explicitRefreshQueued ||= explicit;
    if (this.rpcJobs.has("background") && (explicit || changed && this.rpcHasRead)) this.refreshQueued = true;
    return this.run(async (signal, guard) => {
    const saved = this.journal.read();
    if (saved.intents.some(i => i.outcome) || saved.claims.some(c => c.outcome)) {
      await this.journal.pruneUnsent(guard).catch(() => false); guard();
    }
    this.loadJournal();
    this.reports.clear();
    const localHashes = new Set<string>();
    // Resolve known hashes without re-sending. Unknown/no-hash intents stay visible.
    for (const intent of this.state.intents.filter(item => item.sourceHash && !this.journal.reconciled(item)).slice(0, 8)) {
      const hash = intent.sourceHash!;
      localHashes.add(hash);
      const stillRecoverable = () => this.journal.hasLocal(`intent:${intent.id}`) &&
        (this.journal.sync?.canRecoverSource(hash) ?? true);
      if (!stillRecoverable()) continue;
      await this.journal.sync?.retain(intent, stillRecoverable); guard();
      if (!stillRecoverable()) continue;
      try {
        const rows = await this.client.importHash(this.account, intent.direction, intent.sourceHash!, signal); guard();
        await this.journal.withRecordLock(async () => {
          guard();
          if (stillRecoverable()) this.merge(rows);
        });
      } catch (error) { guard();
        await this.journal.sync?.observePending(intent, !(error instanceof Error && error.message === "BRIDGE_SOURCE_NOT_CONFIRMED")); guard();
      }
    }
    if (this.api) await this.importRemotePending([...this.remote.values()].filter(item => !item.row && !localHashes.has(item.sourceHash))
      .sort((a, b) => (this.attempts.get(a.sourceHash) ?? 0) - (this.attempts.get(b.sourceHash) ?? 0)).slice(0, 4), signal, guard);
    await this.verifyNext(signal, guard); guard();
    const recheck = this.explicitRefreshQueued; this.explicitRefreshQueued = false;
    await this.verifyClaims(signal, guard, recheck ? "all" : undefined); guard();
    if (this.api) { await this.synchronize(signal, guard); return; }
    const scans = await Promise.allSettled([this.scan("in", false, signal, guard), this.scan("out", false, signal, guard)]);
    guard();
    for (const scan of scans) if (scan.status === "rejected") throw scan.reason;
    await this.verifyNext(signal, guard);
    });
  };
  loadOlder = (direction: BridgeDirection) => this.api ? this.requestList(true) : this.run(async (signal, guard) => {
    await this.scan(direction, true, signal, guard); await this.verifyNext(signal, guard);
  }, `older:${direction}`);
  discover = (direction: BridgeDirection) => this.run(async (signal, guard) => {
    await this.scan(direction, false, signal, guard); await this.verifyNext(signal, guard); await this.synchronize(signal, guard);
  }, `discover:${direction}`);
  select = (id: string | null) => {
    this.selected = id;
    if (!this.api || !id) return;
    this.check(id).catch(() => {});
  };
  check = (id: string) => {
    const item = [...this.remote.values()].find(i => i.row?.record.id === id);
    if (item) this.requestDetail(item).catch(() => {});
    return this.run(async (signal, guard) => {
    this.reports.clear();
    await this.verifyNext(signal, guard, [id]);
    for (const row of this.state.claims ?? []) if (row.intent.recordId === id) this.claimAttempts.delete(row.intent.id);
    this.publish({ claims: this.state.claims?.map(row => row.intent.recordId === id ? { ...row, checkedAt: null } : row) ?? [] });
    await this.verifyClaims(signal, guard, id);
    await this.synchronize(signal, guard);
    }, `check:${id}`);
  };
  importClaimHash = (recordId: string, raw: string, intentId?: string) => this.run(async (signal, guard) => {
    try {
      const hash = hashSchema.parse(raw.trim());
      const record = this.state.rows.find(row => row.record.id === recordId)?.record;
      if (!record || !this.client.verifyClaim) throw new Error("Withdrawal unavailable");
      const status = await this.client.verifyClaim(record, hash, signal); guard();
      if (status === "pending") throw new Error("Claim not confirmed yet");
      // A completed shared row may already have left the local journal. The
      // claim verifier independently rebound its canonical source before this.
      await this.journal.withRecordLock(async () => { guard(); this.journal.put(record); });
      guard();
      const saved = this.journal.read().claims;
      const intent = intentId ? saved.find(item => item.id === intentId && item.recordId === recordId) :
        saved.find(item => item.recordId === recordId && item.account === this.account && item.transactionHash === hash) ??
          await this.journal.reserveClaim(record, this.freshlyCompletedClaims);
      guard(); if (!intent || intent.outcome || intent.transactionHash && intent.transactionHash !== hash) throw new Error("Claim intent mismatch");
      let remote = [...this.remote.values()].find(item => item.row?.record.id === recordId && item.account === this.account);
      if (remote && this.api) {
        this.requestDetail(remote).catch(() => {});
        remote = this.remote.get(remote.clientRequestId) ?? remote;
      }
      if (remote) {
        await this.journal.sync?.linkClaim(record, { intent: { ...intent, transactionHash: hash }, state: status, checkedAt: Date.now() }, remote); guard();
      }
      await this.journal.attachClaimHash(intent.id, hash); guard(); this.loadJournal();
      this.claimAttempts.delete(intent.id);
      await this.verifyClaims(signal, guard); await this.verifyNext(signal, guard, [recordId]); guard();
      await this.synchronize(signal, guard);
      this.publish({ message: "Claim transaction verified and linked to this withdrawal." });
    } catch { guard(); this.publish({ message: "Could not verify this claim hash for the selected withdrawal. It may still be pending. Nothing was sent." }); }
  }, `claim:${recordId}`);
  importHash = (direction: BridgeDirection, raw: string, intentId?: string) => this.run(async (signal, guard) => {
    const parsed = hashSchema.safeParse(raw.trim());
    if (!parsed.success) { this.publish({ message: "Enter a transaction hash with 0x followed by 64 hexadecimal characters." }); return; }
    try {
      const records = await this.client.importHash(this.account, direction, parsed.data, signal); guard();
      if (intentId) {
        const intent = this.state.intents.find(item => item.id === intentId);
        if (!intent || !records.some(row => row.direct && row.account === intent.account && row.direction === intent.direction && row.amountWei === intent.amountWei))
          throw new Error("BRIDGE_INTENT_MISMATCH");
        await this.journal.attachHash(intentId, parsed.data); guard();
      }
      await this.journal.withRecordLock(async () => { guard(); this.merge(records); }); await this.verifyNext(signal, guard, records.map(row => row.id)); guard();
      await this.synchronize(signal, guard);
      this.publish({ message: "Source transaction verified and saved for tracking." });
    } catch {
      guard(); this.publish({ message: "Could not verify this source transaction for your wallet. Check the source network and hash; it may still be pending or use an unsupported route. Nothing was sent." });
    }
  }, `source:${direction}:${raw.trim()}`);
  private publishApi(patch: Partial<NonNullable<HistorySnapshot["api"]>>) {
    this.publish({ api: { error: null, syncError: null, hasMore: false, total: 0, loaded: false, pending: [],
      loading: false, loadingOlder: false, olderError: null, detailError: null, syncing: false, ...this.state.api, ...patch } });
  }
  private mergeRemote(items: HistoryItem[], head = false) {
    let changed = false;
    items = items.map(item => {
      const previous = this.remote.get(item.clientRequestId);
      return previous && previous.revision > item.revision ? previous : item;
    });
    if (head) this.remote = new Map([...items.map(i => [i.clientRequestId, i] as const), ...[...this.remote].filter(([id]) => !items.some(i => i.clientRequestId === id))]);
    else for (const item of items) this.remote.set(item.clientRequestId, item);
    const rows = new Map(this.state.rows.map(row => [row.record.id, row]));
    const localClaims = new Set(this.journal.read().claims.map(c => c.id));
    for (const item of items) {
      if (item.row && !rows.has(item.row.record.id)) {
        changed = true;
        rows.set(item.row.record.id, { record: item.row.record, state: "checking", checkedAt: null });
      } else if (item.row && JSON.stringify(rows.get(item.row.record.id)!.record) !== JSON.stringify(item.row.record)) {
        if (JSON.stringify(this.candidates.get(item.row.record.id)) !== JSON.stringify(item.row.record)) {
          changed = true;
          this.candidates.set(item.row.record.id, item.row.record); this.attempts.delete(item.row.record.id);
        }
      } else if (item.row) {
        this.candidates.delete(item.row.record.id);
      }
      if (item.row) {
        const local = rows.get(item.row.record.id)!;
        rows.set(item.row.record.id, { ...local, reported: { state: item.row.state, checkedAt: item.row.checkedAt,
          savedAt: item.savedAt, claimEstimate: item.claimEstimate } });
      }
      for (const claim of item.claims) if (!localClaims.has(claim.intent.id)) {
        const previous = this.reportedClaims.get(claim.intent.id);
        // Conflicting remote updates never overwrite a locally verified association.
        if (!previous || !previous.intent.transactionHash && claim.intent.transactionHash && previous.intent.createdAt === claim.intent.createdAt && previous.intent.recordId === claim.intent.recordId) {
          changed = true;
          this.reportedClaims.set(claim.intent.id, { intent: claim.intent, state: claim.intent.transactionHash ? "checking" : "uncertain", checkedAt: null });
          this.claimAttempts.delete(claim.intent.id);
        }
      }
    }
    const ordered = [...this.remote.values()].flatMap(i => i.row ? [i.row.record.id] : []);
    const ranks = new Map(ordered.map((id, index) => [id, index]));
    const rank = (id: string) => ranks.get(id) ?? -1;
    this.publish({ rows: [...rows.values()].sort((a, b) => rank(a.record.id) - rank(b.record.id)) });
    this.publishApi({ pending: [...this.remote.values()].filter(i => !i.row) });
    this.loadJournal();
    return changed;
  }
  private requestList(older: boolean): Promise<void> {
    if (!this.api || !this.available() || older && !this.cursor) return Promise.resolve();
    const key = older ? "older" : "head", existing = this.listJobs.get(key);
    if (existing) return existing;
    const generation = this.generation, signal = this.context.signal;
    const guard = () => this.contextGuard(generation, signal);
    const task = this.loadApi(older, signal, guard).finally(() => {
      if (this.listJobs.get(key) !== task) return;
      this.listJobs.delete(key);
      if (!older && this.available()) {
        clearTimeout(this.listTimer);
        this.listTimer = setTimeout(() => { this.requestList(false).catch(() => {}); }, 30_000);
      }
    });
    this.listJobs.set(key, task); return task;
  }
  private async loadApi(older: boolean, signal: AbortSignal, guard: () => void) {
    if (older && !this.cursor) return;
    const cursor = this.cursor, cursorRevision = this.cursorRevision;
    this.publishApi(older ? { loadingOlder: true, olderError: null } : { loading: true, error: null });
    try {
      const page = await this.api!.list(this.account, signal, "all", older ? cursor! : undefined); guard();
      const gap = !older && this.state.api?.loaded && this.remote.size > 0 && page.items.length > 0 && !page.items.some(i => this.remote.has(i.clientRequestId));
      const changed = this.mergeRemote(page.items, !older);
      if (older ? cursorRevision === this.cursorRevision && this.cursor === cursor : !this.state.api?.loaded || gap) {
        this.cursor = page.nextCursor; this.cursorRevision++;
      }
      if (gap && page.hasMore) this.publishApi({ continuationNotice: "New registrations extend beyond the refreshed head. Load older replays from this head to fill the gap; previously loaded transfers are retained." });
      else if (older && !page.hasMore) this.publishApi({ continuationNotice: null });
      this.publishApi({ loaded: true, total: page.total, hasMore: this.cursor !== null, ...(older ? { olderError: null } : { error: null }) });
      if (changed || page.items.some(item => !item.row)) this.refreshRpc(false, true).catch(() => {});
      if (!older) this.refreshDetails(signal, guard).catch(() => {});
    } catch (error) {
      if (signal.aborted) return;
      guard();
      if (error instanceof HistoryApiError && error.code === "INVALID_CURSOR") { this.cursor = null; this.cursorRevision++; }
      const message = error instanceof HistoryApiError && error.code === "INVALID_CURSOR" ? "History cursor expired. Refresh to replay pagination; loaded transfers are retained." : "Shared history unavailable. Loaded transfers and local recovery are retained.";
      this.publishApi(older ? { olderError: message } : { error: message });
      if (error instanceof HistoryApiError && error.code === "INVALID_CURSOR") this.publishApi({ loaded: false, hasMore: false });
    } finally {
      if (!signal.aborted && this.available()) this.publishApi(older ? { loadingOlder: false } : { loading: false });
    }
  }
  private requestDetail(item: HistoryItem): Promise<void> {
    if (!this.api || !this.available()) return Promise.resolve();
    const existing = this.detailJobs.get(item.clientRequestId); if (existing) return existing;
    const generation = this.generation, signal = this.context.signal;
    const task = (async () => {
      try {
        const detail = await this.api!.detail(this.account, item.clientRequestId, signal); this.contextGuard(generation, signal);
        const changed = this.mergeRemote([detail]); this.publishApi({ detailError: null });
        if (changed) this.refreshRpc(false, true).catch(() => {});
      } catch { if (!signal.aborted && generation === this.generation) this.publishApi({ detailError: "Some transfer reports could not be refreshed. Loaded transfers are retained." }); }
    })().finally(() => { if (this.detailJobs.get(item.clientRequestId) === task) this.detailJobs.delete(item.clientRequestId); });
    this.detailJobs.set(item.clientRequestId, task); return task;
  }
  private async refreshDetails(signal: AbortSignal, guard: () => void) {
    const candidates = [...this.remote.values()].filter(i => !i.row || i.row.record.id === this.selected || this.state.rows.some(r => r.record.id === i.row!.record.id && !terminal(r)))
      .sort((a, b) => (this.detailAttempts.get(a.clientRequestId) ?? 0) - (this.detailAttempts.get(b.clientRequestId) ?? 0)).slice(0, 4);
    for (const item of candidates) {
      this.detailAttempts.set(item.clientRequestId, Date.now());
      guard(); await this.requestDetail(item); guard();
    }
  }
  private async importRemotePending(items: HistoryItem[], signal: AbortSignal, guard: () => void) {
    for (const item of items) try {
      this.attempts.set(item.sourceHash, Date.now());
      const records = await this.client.importHash(this.account, item.direction, item.sourceHash, signal); guard();
      const rows = new Map(this.state.rows.map(r => [r.record.id, r]));
      for (const record of records) if (!rows.has(record.id)) rows.set(record.id, { record, state: "checking", checkedAt: null });
      this.publish({ rows: [...rows.values()] });
    } catch { guard(); }
  }
  private async synchronize(signal: AbortSignal, guard: () => void) {
    if (!this.journal.sync) return;
    const rows = this.state.rows.filter(r => this.reports.has(r.record.id));
    const estimates = this.client.estimates ? await this.client.estimates(rows, signal) : {};
    guard();
    const scoped = Object.fromEntries(rows.filter(row => row.record.direction === "out").map(row => {
      const estimate = estimates[row.record.id] ?? null;
      const previous = row.reported?.claimEstimate ?? this.state.estimates?.[row.record.id];
      if (previous?.state === "estimated" && previous.estimatedClaimableAt &&
        (!estimate || estimate.state === "unknown") && estimate?.refresh?.state !== "invalidated")
        return [row.record.id, { ...previous, refresh: estimate?.refresh ?? { state: "unavailable",
          checkedAt: estimate?.calculatedAt ?? new Date().toISOString(), reason: estimate?.reason ?? "Claim estimate could not be updated." } }];
      return [row.record.id, estimate];
    }));
    this.publish({ estimates: { ...this.state.estimates, ...scoped } });
    this.queueObservations(rows, scoped);
    this.reports.clear();
  }
  private syncClaims() {
    const localClaims = this.journal.read().claims;
    return (this.state.claims ?? []).filter(c => localClaims.some(i => JSON.stringify(i) === JSON.stringify(c.intent)) ||
      this.reportedClaims.has(c.intent.id) && c.checkedAt !== null && ["reverted", "claimed"].includes(c.state));
  }
  private queueObservations(rows: HistoryRow[], estimates?: Record<string, ClaimEstimate | null>) {
    if (!this.journal.sync || !this.available()) return;
    const required = new Set(this.journal.sync.verificationNeeded()), claims = this.syncClaims();
    for (const row of rows) {
      const signature = JSON.stringify([row.record, row.state, claims.filter(c => c.intent.recordId === row.record.id).map(c => [c.intent, c.state])]);
      const changed = this.reportSignatures.get(row.record.id) !== signature;
      const estimate = estimates?.[row.record.id];
      const previous = this.pendingRows.get(row.record.id);
      const previousEstimate = previous?.estimate;
      const nextEstimate = estimate !== undefined ? estimate : previousEstimate;
      if (changed || required.has(row.record.id) || Date.now() - (this.reportTimes.get(row.record.id) ?? 0) >= 30_000 || estimate !== undefined) {
        const local = this.reports.get(row.record.id) || (previous?.local ?? this.journal.hasLocal(`record:${row.record.id}`));
        this.pendingRows.set(row.record.id, { row, local, ...(nextEstimate !== undefined ? { estimate: nextEstimate } : {}) });
        this.reportSignatures.set(row.record.id, signature);
      }
    }
    this.observePending();
  }
  private observePending() {
    if (this.observationJob || !this.pendingRows.size || !this.available()) return;
    const generation = this.generation, signal = this.context.signal;
    const task = (async () => {
      while (this.pendingRows.size) {
        this.contextGuard(generation, signal);
        const pending = [...this.pendingRows.values()]; this.pendingRows.clear();
        const rows = pending.map(p => p.row), estimates = Object.fromEntries(pending.filter(p => p.estimate !== undefined).map(p => [p.row.record.id, p.estimate!]));
        const local = new Set(pending.filter(p => p.local).map(p => p.row.record.id));
        await this.journal.sync!.observe(rows, () => this.syncClaims(), Object.keys(estimates).length ? estimates : undefined, signal,
          row => !local.has(row.record.id) || this.journal.hasLocal(`record:${row.record.id}`));
        this.contextGuard(generation, signal);
        for (const row of rows) this.reportTimes.set(row.record.id, Date.now());
        this.requestPost();
      }
    })().catch(() => { if (!signal.aborted && generation === this.generation) this.publishApi({ syncError: "History updates could not be queued. Local recovery is retained." }); })
      .finally(() => {
        if (this.observationJob !== task) return;
        this.observationJob = null; this.observePending(); this.requestPost();
      });
    this.observationJob = task;
  }
  private requestPost() {
    if (!this.journal.sync || !this.available()) return;
    if (this.postJob) { this.postQueued = true; return; }
    clearTimeout(this.postTimer);
    const generation = this.generation, signal = this.context.signal;
    this.publishApi({ syncing: true });
    const task = (async () => {
      do {
        this.postQueued = false;
        await this.journal.sync!.flush(signal); this.contextGuard(generation, signal);
        this.publishApi({ syncError: this.journal.sync!.error });
        await this.reconcileCompleted(() => this.contextGuard(generation, signal));
      } while (this.postQueued && this.available());
    })().catch(() => { if (!signal.aborted && generation === this.generation) this.publishApi({ syncError: "History synchronization is unavailable. Recovery is retained." }); })
      .finally(() => {
        if (this.postJob !== task) return;
        this.postJob = null; this.publishApi({ syncing: false });
        if (this.available()) this.postTimer = setTimeout(() => this.requestPost(), 30_000);
      });
    this.postJob = task;
  }
  private async reconcileCompleted(guard: () => void) {
    if (!this.journal.sync || this.observationJob || this.pendingRows.size) return;
    // Streamed rows can be acknowledged while their verification batch and
    // estimate are still running. Revisit cleanup once that RPC job finishes.
    if (this.rpcJob) { this.cleanupDeferred = true; return; }
    const fresh = (checkedAt: number | null) => checkedAt !== null && Date.now() >= checkedAt && Date.now() - checkedAt < 300_000;
    const claims = this.syncClaims().filter(c => c.intent.outcome || fresh(c.checkedAt));
    for (const row of this.state.rows) if (terminal(row) && fresh(row.checkedAt) && this.journal.sync.acknowledged(row, claims)) {
      // A spent withdrawal can settle the action guard without proving the
      // receipt of every local attempt. Keep those hashes/prompts recoverable.
      const observed = claims.filter(c => c.intent.recordId === row.record.id);
      const completedClaims = new Set(observed.filter(c => c.intent.transactionHash && ["claimed", "reverted"].includes(c.state)).map(c => c.intent.id));
      // HTTP success alone never settles an unresolved operation.
      const reconciled = await this.journal.pruneCompleted(row.record, completedClaims, { row, claims: observed, current: () => {
        guard();
        if (this.rpcJob) { this.cleanupDeferred = true; return false; }
        return !this.observationJob && !this.pendingRows.size && this.state.rows.includes(row) &&
          fresh(row.checkedAt) && observed.every(claim => this.state.claims?.includes(claim) &&
            (claim.intent.outcome || fresh(claim.checkedAt)));
      } }); guard();
      if (reconciled) this.loadJournal();
    }
  }
}

export function browserStorage(): Storage | null { try { return window.localStorage; } catch { return null; } }
export class BridgeHistoryRegistry {
  private sessions = new Map<string, BridgeHistoryController>();
  constructor(private apiFactory: () => HistoryApi | null = () => new HistoryApi()) {}
  get(account: Address, childRpc: string, parentRpc: string, client: BridgeHistoryClient) {
    const key = JSON.stringify([getAddress(account), childRpc, parentRpc, historyApiBase()]);
    let session = this.sessions.get(key);
    if (!session) {
      const storage = browserStorage(), journal = new BridgeJournal({ account, childRpc, parentRpc }, storage);
      const api = this.apiFactory();
      if (api) journal.sync = new BridgeHistorySync(journal.prefix, api, storage, browserLock);
      session = new BridgeHistoryController(getAddress(account), journal, client);
      this.sessions.set(key, session);
    }
    return session;
  }
  dispose() { for (const session of this.sessions.values()) session.stop(); this.sessions.clear(); }
}
